import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';

import SafeIcon from '../../common/SafeIcon';
import {
  transactionImportService,
} from '../../services/transactionImportService';

const {
  FiAlertCircle,
  FiCheckCircle,
  FiChevronLeft,
  FiChevronRight,
  FiLoader,
  FiX,
} = FiIcons;

/*
 * ---------------------------------------------------------
 * Formatting helpers
 * ---------------------------------------------------------
 */

function formatDate(value) {
  if (!value) {
    return '—';
  }

  const [year, month, day] =
    String(value).split('-');

  if (year && month && day) {
    return new Intl.DateTimeFormat(
      'en-US',
      {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      }
    ).format(
      new Date(
        Date.UTC(
          Number(year),
          Number(month) - 1,
          Number(day)
        )
      )
    );
  }

  return String(value);
}

function formatMoney(
  value,
  currency = 'USD'
) {
  const amount = Number(value || 0);

  const normalizedCurrency =
    String(
      currency || 'USD'
    ).toUpperCase();

  try {
    return new Intl.NumberFormat(
      'en-US',
      {
        style: 'currency',
        currency: normalizedCurrency,
      }
    ).format(amount);
  } catch {
    return new Intl.NumberFormat(
      'en-US',
      {
        style: 'currency',
        currency: 'USD',
      }
    ).format(amount);
  }
}

function formatConfidence(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return '—';
  }

  const numericValue =
    Number(value);

  if (
    !Number.isFinite(
      numericValue
    )
  ) {
    return '—';
  }

  return `${Math.round(
    numericValue <= 1
      ? numericValue * 100
      : numericValue
  )}%`;
}

function statusClass(status) {
  if (
    status === 'categorized'
  ) {
    return 'bg-emerald-50 text-emerald-700';
  }

  if (
    status === 'needs_review' ||
    status === 'duplicate'
  ) {
    return 'bg-amber-50 text-amber-700';
  }

  if (
    status === 'approved'
  ) {
    return 'bg-blue-50 text-blue-700';
  }

  if (
    status === 'imported'
  ) {
    return 'bg-violet-50 text-violet-700';
  }

  return 'bg-slate-100 text-slate-600';
}

/*
 * ---------------------------------------------------------
 * Small UI components
 * ---------------------------------------------------------
 */

function SummaryCard({
  label,
  value,
  tone = 'slate',
}) {
  const colors = {
    slate:
      'bg-slate-50 text-slate-900',
    green:
      'bg-emerald-50 text-emerald-700',
    amber:
      'bg-amber-50 text-amber-700',
    rose:
      'bg-rose-50 text-rose-700',
    blue:
      'bg-blue-50 text-blue-700',
    violet:
      'bg-violet-50 text-violet-700',
  };

  return (
    <div
      className={`rounded-2xl p-4 ${colors[tone]}`}
    >
      <p className="text-xs font-medium opacity-70">
        {label}
      </p>

      <p className="mt-1 text-2xl font-semibold">
        {value}
      </p>
    </div>
  );
}

function ProgressStep({
  number,
  label,
  active,
  completed,
}) {
  return (
    <div className="flex min-w-0 flex-1 items-center">
      <div
        className={`flex items-center gap-2 ${
          active || completed
            ? 'text-slate-950'
            : 'text-slate-400'
        }`}
      >
        <div
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
            completed
              ? 'bg-emerald-600 text-white'
              : active
              ? 'bg-slate-950 text-white'
              : 'bg-slate-100 text-slate-400'
          }`}
        >
          {completed ? (
            <SafeIcon
              icon={FiCheckCircle}
            />
          ) : (
            number
          )}
        </div>

        <div className="min-w-0">
          <p className="truncate text-xs font-semibold">
            {label}
          </p>
        </div>
      </div>
    </div>
  );
}

/*
 * ---------------------------------------------------------
 * Review modal
 * ---------------------------------------------------------
 */

export default function ReviewImportTransactionsModal({
  isOpen,
  onClose,
  organizationId,
  importBatchId,
  organizationCurrency,

  /*
   * Internal bank_accounts.id for the bank selected
   * by the user on the Connect Bank page.
   *
   * Example:
   * bank_accounts.id
   *   -> BOA Checking
   */
  sourceBankAccountId,

  /*
   * Optional accounting.accounts.id fallback.
   *
   * This is useful when the service already resolves
   * bank_accounts.account_id before returning review rows.
   */
  sourceAccountId,

  /*
   * Approval props are retained for compatibility
   * with the parent component.
   *
   * Approval is intentionally NOT implemented here yet.
   */
  approvalLoading: externalApprovalLoading,
  approvalResult,
  onApprovalStart,
  onApprovalComplete,
  onApprovalError,
}) {
  const [step, setStep] =
    useState(1);

  const [batch, setBatch] =
    useState(null);

  const [rows, setRows] =
    useState([]);

  const [accounts, setAccounts] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [savingId, setSavingId] =
    useState(null);

  const [selectedRows, setSelectedRows] =
    useState([]);

  const [error, setError] =
    useState('');

  const [successMessage, setSuccessMessage] =
    useState('');
  /*
   * Approval remains disabled for this stage.
   */
  const approvalLoading =
    Boolean(
      externalApprovalLoading
    );

  const [approvalAction, setApprovalAction] = useState(null);

  /*
   * -------------------------------------------------------
   * Determine whether a row belongs to the selected bank.
   *
   * The preferred authoritative field is:
   *
   *   source_bank_account_id
   *
   * The fallback is:
   *
   *   sourceBankAccountId
   *
   * If the service returns only source_account_id,
   * sourceAccountId can be used as a fallback.
   * -------------------------------------------------------
   */

  const rowBelongsToSelectedSource =
    (row) => {
      /*
       * If no source filter was supplied,
       * do not filter the batch.
       */
      if (
        !sourceBankAccountId &&
        !sourceAccountId
      ) {
        return true;
      }

      const rowBankAccountId =
        row?.source_bank_account_id ??
        row?.sourceBankAccountId ??
        row?.bank_account_id ??
        row?.bankAccountId ??
        null;

      /*
       * Preferred comparison:
       * internal bank_accounts.id
       */
      if (
        sourceBankAccountId &&
        rowBankAccountId
      ) {
        return String(
          rowBankAccountId
        ) ===
          String(
            sourceBankAccountId
          );
      }

      /*
       * Fallback:
       * accounting.accounts.id
       *
       * source_account_id represents the accounting
       * source account, NOT the bank_accounts row.
       */
      if (
        sourceAccountId &&
        row?.source_account_id
      ) {
        return String(
          row.source_account_id
        ) ===
          String(
            sourceAccountId
          );
      }

      /*
       * If the service has not provided the source
       * bank-account identifier, do not silently hide
       * rows on the client.
       *
       * The authoritative filtering should happen
       * in getImportReviewData().
       */
      return true;
    };

  /*
   * -------------------------------------------------------
   * Load authoritative review data
   * -------------------------------------------------------
   */

  const loadReviewData =
    async ({
      showLoading = true,
      mountedCheck = () => true,
    } = {}) => {
      if (
        !organizationId ||
        !importBatchId
      ) {
        return;
      }

      if (showLoading) {
        setLoading(true);
      }

      try {
        /*
         * The service should support:
         *
         * getImportReviewData(
         *   organizationId,
         *   importBatchId,
         *   sourceBankAccountId
         * )
         *
         * The third argument allows the backend/service
         * to keep the review scoped to the bank selected
         * by the user.
         */
        const [
          review,
          availableAccounts,
        ] =
          await Promise.all([
            transactionImportService.getImportReviewData(
              organizationId,
              importBatchId,
              sourceBankAccountId || null
            ),

            transactionImportService.getImportAccounts(
              organizationId
            ),
          ]);

        if (!mountedCheck()) {
          return;
        }

        const reviewRows =
          Array.isArray(
            review?.rows
          )
            ? review.rows
            : [];

        /*
         * Safe fallback filtering.
         *
         * Normally the service should already return
         * only the requested source account.
         */
        const filteredRows =
          reviewRows.filter(
            row =>
              rowBelongsToSelectedSource(
                row
              )
          );

        setBatch(
          review?.batch || null
        );

        setRows(
          filteredRows
        );

        setAccounts(
          Array.isArray(
            availableAccounts
          )
            ? availableAccounts
            : []
        );
      } catch (loadError) {
        if (mountedCheck()) {
          setError(
            loadError?.message ||
              'The staged import transactions could not be loaded.'
          );
        }
      } finally {
        if (
          mountedCheck() &&
          showLoading
        ) {
          setLoading(false);
        }
      }
    };

  /*
   * Initial load.
   */
  useEffect(() => {
    let mounted = true;

    if (
      !isOpen ||
      !organizationId ||
      !importBatchId
    ) {
      return undefined;
    }

    setStep(1);
    setError('');
    setSuccessMessage('');
    setSelectedRows([]);
    setBatch(null);
    setRows([]);
    setAccounts([]);

    loadReviewData({
      showLoading: true,
      mountedCheck: () =>
        mounted,
    });

    return () => {
      mounted = false;
    };
  }, [
    isOpen,
    organizationId,
    importBatchId,
    sourceBankAccountId,
    sourceAccountId,
  ]);

  /*
   * -------------------------------------------------------
   * Summary
   * -------------------------------------------------------
   */

  const summary = useMemo(() => {
    return {
      total: rows.length,

      categorized:
        rows.filter(
          row =>
            row.status ===
            'categorized'
        ).length,

      needsReview:
        rows.filter(
          row =>
            row.status ===
            'needs_review'
        ).length,

      duplicates:
        rows.filter(
          row =>
            row.is_duplicate ||
            row.status ===
              'duplicate'
        ).length,

      approved:
        rows.filter(
          row =>
            row.status ===
            'approved'
        ).length,

      imported:
        rows.filter(
          row =>
            row.status ===
            'imported'
        ).length,
    };
  }, [rows]);

  /*
   * -------------------------------------------------------
   * Source account
   *
   * IMPORTANT:
   *
   * sourceAccount is the accounting source account.
   *
   * It is NOT the suggested accounting category.
   * -------------------------------------------------------
   */

  const sourceAccount =
    useMemo(() => {
      return rows.find(
        row =>
          row.sourceAccount
      )?.sourceAccount;
    }, [rows]);

  /*
   * -------------------------------------------------------
   * Active rows exclude duplicates.
   * -------------------------------------------------------
   */

  const activeRows =
    useMemo(() => {
      return rows.filter(
        row =>
          !row.is_duplicate &&
          row.status !==
            'duplicate'
      );
    }, [rows]);

  /*
   * -------------------------------------------------------
   * Categorization validation
   *
   * Approval is NOT performed here.
   *
   * This only determines whether the review can
   * proceed to the finish-review step.
   * -------------------------------------------------------
   */

  const reviewValidation =
    useMemo(() => {
      const missingCategoryRows =
        activeRows.filter(
          row =>
            !row.suggested_account_id
        );

      const unresolvedRows =
        activeRows.filter(
          row =>
            ![
              'categorized',
              'approved',
              'imported',
            ].includes(
              row.status
            )
        );

      return {
        valid:
          missingCategoryRows.length ===
            0 &&
          unresolvedRows.length ===
            0,

        totalRows:
          activeRows.length,

        missingCategoryRows,

        unresolvedRows,
      };
    }, [activeRows]);

  /*
   * -------------------------------------------------------
   * Select / deselect individual rows
   *
   * Selection is retained for the future approval
   * workflow but currently has no approval action.
   * -------------------------------------------------------
   */

  const toggleRowSelection =
    rowId => {
      setSelectedRows(
        current =>
          current.includes(rowId)
            ? current.filter(
                id =>
                  id !== rowId
              )
            : [
                ...current,
                rowId,
              ]
      );
    };

  /*
   * -------------------------------------------------------
   * Select all rows that are currently categorized.
   * -------------------------------------------------------
   */

  const selectableRows =
    useMemo(() => {
      return activeRows.filter(
        (row) => row.status === 'categorized'
      );
    }, [activeRows]);

  const selectAllRows = () => {
    setSelectedRows(
      selectableRows.map(
        row =>
          row.id
      )
    );
  };

  const clearSelection = () => {
    setSelectedRows([]);
  };

  const allSelectableSelected =
    selectableRows.length > 0 &&
    selectableRows.every(
      row =>
        selectedRows.includes(
          row.id
        )
    );

  /*
   * -------------------------------------------------------
   * Update suggested accounting account
   * -------------------------------------------------------
   */

  const updateAccount =
    async (
      row,
      value
    ) => {
      if (
        !value ||
        value ===
          row.suggested_account_id
      ) {
        return;
      }

      setSavingId(row.id);
      setError('');
      setSuccessMessage('');

      try {
        const updated =
          await transactionImportService.updateSuggestedAccount(
            {
              organizationId,
              importBatchId,
              importTransactionId:
                row.id,
              suggestedAccountId:
                value,
            }
          );

        setRows(
          currentRows =>
            currentRows.map(
              currentRow =>
                currentRow.id ===
                row.id
                  ? {
                      ...currentRow,

                      /*
                       * Only apply returned fields.
                       *
                       * confidence,
                       * ai_reason,
                       * source_account_id
                       *
                       * remain untouched by the service.
                       */
                      ...updated,
                    }
                  : currentRow
            )
        );

        setSuccessMessage(
          'Suggested account updated successfully.'
        );
      } catch (updateError) {
        setError(
          updateError?.message ||
            'The suggested account could not be updated.'
        );
      } finally {
        setSavingId(null);
      }
    };

  /*
   * -------------------------------------------------------
   * Continue to finish review
   *
   * This does NOT approve anything.
   * -------------------------------------------------------
   */

  const goToFinishReview =
    () => {
      setError('');
      setSuccessMessage('');

      if (
        !reviewValidation.valid
      ) {
        setError(
          'Please resolve all transactions that need review before continuing.'
        );

        return;
      }

      setStep(2);
    };

  /*
   * -------------------------------------------------------
   * Return to categorization
   * -------------------------------------------------------
   */

  const goBackToCategorization =
    () => {
      setError('');
      setSuccessMessage('');
      setStep(1);
    };

  /*
   * -------------------------------------------------------
   * Approval intentionally disabled.
   *
   * These handlers are retained only so the component
   * remains compatible with the parent while approval
   * is not yet implemented.
   * -------------------------------------------------------
   */

  // ReviewImportTransactionsModal.jsx
// Approval handlers.

  const approveSelected = async () => {
    if (approvalAction) return;

    const selectedIds = selectedRows.filter(Boolean);

    if (selectedIds.length === 0) {
      setError('Please select at least one transaction to approve.');
      return;
    }

    if (!organizationId || !importBatchId) {
      setError('Organization and import batch are required.');
      return;
    }

    try {
      setError('');
      setSuccessMessage('');
      setApprovalAction('selected');

      onApprovalStart?.();

      const result = await transactionImportService.approveImport({
        organizationId,
        importBatchId,
        mode: 'selected',
        importTransactionIds: selectedIds,
      });

      setSuccessMessage(
        result?.message ||
          'Selected transactions were approved successfully.'
      );

      onApprovalComplete?.(result);
    } catch (approvalError) {
      console.error(
        '[ReviewImportTransactionsModal] Selected approval failed:',
        approvalError
      );

      setError(
        approvalError?.message ||
          'Unable to approve the selected transactions.'
      );

      onApprovalError?.(approvalError);
    } finally {
      setApprovalAction(null);
    }
  };

  const approveAll = async () => {
    if (approvalAction) return;

    if (!organizationId || !importBatchId) {
      setError('Organization and import batch are required.');
      return;
    }

    try {
      setError('');
      setSuccessMessage('');
      setApprovalAction('all');

      onApprovalStart?.();

      const result = await transactionImportService.approveImport({
        organizationId,
        importBatchId,
        mode: 'all',
        importTransactionIds: [],
      });

      setSuccessMessage(
        result?.message ||
          'All eligible transactions were approved successfully.'
      );

      onApprovalComplete?.(result);
    } catch (approvalError) {
      console.error(
        '[ReviewImportTransactionsModal] Approve all failed:',
        approvalError
      );

      setError(
        approvalError?.message ||
          'Unable to approve the transactions.'
      );

      onApprovalError?.(approvalError);
    } finally {
      setApprovalAction(null);
    }
  };

  /*
   * -------------------------------------------------------
   * Reload authoritative review data.
   * -------------------------------------------------------
   */

  const reloadReview =
    async () => {
      if (
        !organizationId ||
        !importBatchId
      ) {
        return;
      }

      setError('');

      await loadReviewData({
        showLoading: false,
        mountedCheck: () =>
          true,
      });
    };

  /*
   * -------------------------------------------------------
   * Close
   * -------------------------------------------------------
   */

  const handleClose =
    () => {
      if (approvalLoading) {
        return;
      }

      onClose?.();
    };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
      <motion.div
        initial={{
          opacity: 0,
        }}
        animate={{
          opacity: 1,
        }}
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      <motion.section
        initial={{
          opacity: 0,
          y: 16,
          scale: 0.98,
        }}
        animate={{
          opacity: 1,
          y: 0,
          scale: 1,
        }}
        className="relative z-10 flex max-h-[92vh] w-full max-w-7xl flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl"
      >
        {/* HEADER */}
        <header className="border-b border-slate-100 px-5 py-4 md:px-7">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">
                Import Review
              </p>

              <h2 className="mt-1 text-xl font-semibold text-slate-950">
                Review Imported Transactions
              </h2>

              <p className="mt-1 text-xs text-slate-500">
                Review AI categorization before approval and posting.
              </p>
            </div>

            <button
              type="button"
              onClick={handleClose}
              disabled={
                approvalLoading
              }
              className="rounded-xl p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Close transaction review"
            >
              <SafeIcon
                icon={FiX}
              />
            </button>
          </div>

          {/* PROGRESS */}
          <div className="mt-5 flex items-center gap-3">
            <ProgressStep
              number="1"
              label="Categorization"
              active={step === 1}
              completed={step > 1}
            />

            <div
              className={`h-px flex-1 ${
                step > 1
                  ? 'bg-emerald-300'
                  : 'bg-slate-200'
              }`}
            />

            <ProgressStep
              number="2"
              label="Finish Review"
              active={step === 2}
              completed={
                summary.imported ===
                  activeRows.length &&
                activeRows.length >
                  0
              }
            />
          </div>
        </header>

        {/* CONTENT */}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 md:px-7">
          {error && (
            <div className="mb-5 flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              <SafeIcon
                icon={FiAlertCircle}
                className="mt-0.5 shrink-0"
              />

              <p>{error}</p>
            </div>
          )}

          {successMessage &&
            !error && (
              <div className="mb-5 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                <SafeIcon
                  icon={FiCheckCircle}
                  className="mt-0.5 shrink-0"
                />

                <p>
                  {successMessage}
                </p>
              </div>
            )}

          {loading ? (
            <div className="flex min-h-[280px] items-center justify-center gap-3 text-sm text-slate-500">
              <SafeIcon
                icon={FiLoader}
                className="animate-spin"
              />

              Loading staged transactions...
            </div>
          ) : (
            <>
              {/* STEP 1 */}
              {step === 1 && (
                <>
                  <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">
                        Source Account
                      </p>

                      <p className="mt-1 text-sm font-semibold text-slate-900">
                        {sourceAccount
                          ? `${
                              sourceAccount.code ||
                              'No code'
                            } - ${
                              sourceAccount.name
                            }`
                          : 'Unavailable'}
                      </p>
                    </div>

                    <p className="text-xs text-slate-500">
                      {batch?.file_name ||
                        'Bank import'}{' '}
                      · staged for review
                    </p>
                  </div>

                  <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
                    <SummaryCard
                      label="Total"
                      value={
                        summary.total
                      }
                    />

                    <SummaryCard
                      label="Categorized"
                      value={
                        summary.categorized
                      }
                      tone="green"
                    />

                    <SummaryCard
                      label="Needs Review"
                      value={
                        summary.needsReview
                      }
                      tone="amber"
                    />

                    <SummaryCard
                      label="Duplicates"
                      value={
                        summary.duplicates
                      }
                      tone="rose"
                    />

                    <SummaryCard
                      label="Approved"
                      value={
                        summary.approved
                      }
                      tone="blue"
                    />

                    <SummaryCard
                      label="Imported"
                      value={
                        summary.imported
                      }
                      tone="violet"
                    />
                  </div>

                  <div
                    className={`mb-5 flex items-start gap-3 rounded-2xl border p-4 text-sm ${
                      reviewValidation.valid
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                        : 'border-amber-200 bg-amber-50 text-amber-800'
                    }`}
                  >
                    <SafeIcon
                      icon={
                        reviewValidation.valid
                          ? FiCheckCircle
                          : FiAlertCircle
                      }
                      className="mt-0.5 shrink-0"
                    />

                    <div>
                      <p className="font-semibold">
                        {reviewValidation.valid
                          ? 'Categorization complete'
                          : 'Transactions need review'}
                      </p>

                      <p className="mt-1 text-xs">
                        {reviewValidation.valid
                          ? 'All active transactions have an accounting account and are ready for the future approval step.'
                          : 'Change the suggested account for every transaction that needs review before continuing.'}
                      </p>
                    </div>
                  </div>

                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">
                        Transactions
                      </p>

                      <p className="text-xs text-slate-500">
                        {selectedRows.length}{' '}
                        selected
                      </p>
                    </div>

                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={
                          allSelectableSelected
                            ? clearSelection
                            : selectAllRows
                        }
                        disabled={
                          approvalLoading ||
                          selectableRows.length ===
                            0
                        }
                        className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {allSelectableSelected
                          ? 'Clear Selection'
                          : 'Select All'}
                      </button>
                    </div>
                  </div>

                  <div className="overflow-x-auto rounded-2xl border border-slate-200">
                    <table className="min-w-[1250px] text-left text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="w-12 px-4 py-3">
                            <input
                              type="checkbox"
                              checked={
                                allSelectableSelected
                              }
                              onChange={() =>
                                allSelectableSelected
                                  ? clearSelection()
                                  : selectAllRows()
                              }
                              disabled={
                                approvalLoading ||
                                selectableRows.length ===
                                  0
                              }
                              className="h-4 w-4 rounded border-slate-300"
                            />
                          </th>

                          {[
                            'Row #',
                            'Date',
                            'Description',
                            'Debit',
                            'Credit',
                            'AI Categorization',
                            'Confidence',
                            'Status',
                          ].map(
                            heading => (
                              <th
                                key={
                                  heading
                                }
                                className="whitespace-nowrap px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500"
                              >
                                {
                                  heading
                                }
                              </th>
                            )
                          )}
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-slate-100">
                        {rows.map(
                          row => {
                            const rowCurrency =
                              row.currency ||
                              organizationCurrency ||
                              'USD';

                            const canSelect =
                              !row.is_duplicate &&
                              row.status !==
                                'duplicate' &&
                              (
                                row.status ===
                                  'categorized' ||
                                row.status ===
                                  'approved'
                              );

                            return (
                              <tr
                                key={
                                  row.id
                                }
                                className="align-middle"
                              >
                                <td className="px-4 py-3">
                                  <input
                                    type="checkbox"
                                    checked={selectedRows.includes(
                                      row.id
                                    )}
                                    onChange={() =>
                                      toggleRowSelection(
                                        row.id
                                      )
                                    }
                                    disabled={
                                      !canSelect ||
                                      approvalLoading
                                    }
                                    className="h-4 w-4 rounded border-slate-300"
                                  />
                                </td>

                                <td className="px-4 py-3 text-slate-400">
                                  {
                                    row.row_number
                                  }
                                </td>

                                <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                                  {formatDate(
                                    row.transaction_date
                                  )}
                                </td>

                                <td className="max-w-[280px] truncate px-4 py-3 text-slate-700">
                                  {row.description ||
                                    '—'}
                                </td>

                                <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                                  {formatMoney(
                                    row.debit,
                                    rowCurrency
                                  )}
                                </td>

                                <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                                  {formatMoney(
                                    row.credit,
                                    rowCurrency
                                  )}
                                </td>

                                <td className="px-4 py-3">
                                  <select
                                    value={
                                      row.suggested_account_id ||
                                      ''
                                    }
                                    onChange={
                                      event =>
                                        updateAccount(
                                          row,
                                          event
                                            .target
                                            .value
                                        )
                                    }
                                    disabled={
                                      savingId ===
                                        row.id ||
                                      row.status ===
                                        'approved' ||
                                      row.status ===
                                        'imported' ||
                                      approvalLoading
                                    }
                                    className="min-w-[220px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 outline-none focus:border-slate-500 disabled:opacity-60"
                                  >
                                    <option
                                      value=""
                                      disabled
                                    >
                                      {savingId ===
                                      row.id
                                        ? 'Saving...'
                                        : 'Select category'}
                                    </option>

                                    {accounts.map(
                                      account => (
                                        <option
                                          key={
                                            account.id
                                          }
                                          value={
                                            account.id
                                          }
                                        >
                                          {account.code ||
                                            'No code'}{' '}
                                          -{' '}
                                          {
                                            account.name
                                          }
                                        </option>
                                      )
                                    )}
                                  </select>
                                </td>

                                <td className="whitespace-nowrap px-4 py-3 font-semibold text-slate-700">
                                  {formatConfidence(
                                    row.confidence
                                  )}
                                </td>

                                <td className="px-4 py-3">
                                  <span
                                    className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusClass(
                                      row.status
                                    )}`}
                                  >
                                    {row.status ||
                                      'pending'}
                                  </span>
                                </td>
                              </tr>
                            );
                          }
                        )}

                        {!rows.length && (
                          <tr>
                            <td
                              colSpan={
                                9
                              }
                              className="px-4 py-12 text-center text-sm text-slate-500"
                            >
                              No staged transactions were found for this import batch.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {/* STEP 2 */}
              {step === 2 && (
                <div className="space-y-5">
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
                    <div className="flex items-start gap-3">
                      <SafeIcon
                        icon={
                          FiCheckCircle
                        }
                        className="mt-0.5 shrink-0 text-emerald-600"
                      />

                      <div>
                        <p className="text-sm font-semibold text-emerald-900">
                          Review complete
                        </p>

                        <p className="mt-1 text-xs leading-5 text-emerald-700">
                          The transactions have been categorized and are ready for the approval step.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
                    <SummaryCard
                      label="Total"
                      value={
                        summary.total
                      }
                    />

                    <SummaryCard
                      label="Categorized"
                      value={
                        summary.categorized
                      }
                      tone="green"
                    />

                    <SummaryCard
                      label="Needs Review"
                      value={
                        summary.needsReview
                      }
                      tone="amber"
                    />

                    <SummaryCard
                      label="Duplicates"
                      value={
                        summary.duplicates
                      }
                      tone="rose"
                    />

                    <SummaryCard
                      label="Approved"
                      value={
                        summary.approved
                      }
                      tone="blue"
                    />

                    <SummaryCard
                      label="Imported"
                      value={
                        summary.imported
                      }
                      tone="violet"
                    />
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-white p-5">
                    <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">
                      Review Summary
                    </p>

                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                      <div>
                        <p className="text-xs text-slate-400">
                          Source Account
                        </p>

                        <p className="mt-1 text-sm font-semibold text-slate-900">
                          {sourceAccount
                            ? `${
                                sourceAccount.code ||
                                'No code'
                              } - ${
                                sourceAccount.name
                              }`
                            : 'Unavailable'}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs text-slate-400">
                          Import Source
                        </p>

                        <p className="mt-1 text-sm font-semibold text-slate-900">
                          {batch?.source ||
                            'Import'}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs text-slate-400">
                          Transactions Ready
                        </p>

                        <p className="mt-1 text-sm font-semibold text-emerald-700">
                          {
                            reviewValidation.totalRows
                          }
                        </p>
                      </div>

                      <div>
                        <p className="text-xs text-slate-400">
                          Selected
                        </p>

                        <p className="mt-1 text-sm font-semibold text-blue-700">
                          {
                            selectedRows.length
                          }
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-blue-200 bg-blue-50 p-5">
                    <div className="flex items-start gap-3">
                      <SafeIcon
                        icon={
                          FiCheckCircle
                        }
                        className="mt-0.5 shrink-0 text-blue-600"
                      />

                      <div>
                        <p className="text-sm font-semibold text-blue-900">
                          Ready for approval
                        </p>

                        
                      </div>
                    </div>
                  </div>

                  {approvalResult?.errors?.length >
                    0 && (
                    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800">
                      <p className="font-semibold">
                        Some transactions could not be imported.
                      </p>

                      <ul className="mt-2 list-disc space-y-1 pl-5 text-xs">
                        {approvalResult.errors.map(
                          (
                            item,
                            index
                          ) => (
                            <li
                              key={
                                index
                              }
                            >
                              {item}
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* FOOTER */}
        <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-5 py-4 md:px-7">
          <p className="text-xs text-slate-500">
            {step === 1
              ? 'Review and correct AI account suggestions before continuing.'
              : 'Categorization is complete. You can approve selected transactions or approve all eligible transactions..'}
          </p>

          <div className="flex flex-wrap gap-2">
            {step === 1 && (
              <>
                <button
                  type="button"
                  onClick={
                    handleClose
                  }
                  disabled={
                    approvalLoading
                  }
                  className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={
                    goToFinishReview
                  }
                  disabled={
                    loading ||
                    savingId !==
                      null ||
                    approvalLoading ||
                    !reviewValidation.valid
                  }
                  className={`inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold transition ${
                    reviewValidation.valid &&
                    !loading &&
                    savingId ===
                      null &&
                    !approvalLoading
                      ? 'bg-slate-950 text-white hover:bg-slate-800'
                      : 'cursor-not-allowed bg-slate-200 text-slate-400'
                  }`}
                >
                  Continue to Finish Review

                  <SafeIcon
                    icon={
                      FiChevronRight
                    }
                  />
                </button>
              </>
            )}

            {step === 2 && (
              <>
                <button
                  type="button"
                  onClick={
                    goBackToCategorization
                  }
                  disabled={
                    approvalLoading
                  }
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
                >
                  <SafeIcon
                    icon={
                      FiChevronLeft
                    }
                  />

                  Back
                </button>

                {/*
                 * Approval intentionally disabled.
                 *
                 * DO NOT connect these buttons to
                 * approve-import yet.
                 */}

                <button
                    type="button"
                    onClick={approveSelected}
                    disabled={
                      approvalAction !== null ||
                      selectedRows.length === 0
                    }
                    className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition ${
                      approvalAction === null &&
                      selectedRows.length > 0
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'cursor-not-allowed bg-slate-200 text-slate-400'
                    }`}
                  >
                    {approvalAction === 'selected' ? (
                      <>
                        <SafeIcon
                          icon={FiLoader}
                          className="animate-spin"
                        />
                        Approving...
                      </>
                    ) : (
                      <>
                        <SafeIcon icon={FiCheckCircle} />
                        Approve Selected
                      </>
                    )}
                  </button>

                  <button
                      type="button"
                      onClick={approveAll}
                      disabled={
                        approvalAction !== null ||
                        selectableRows.length === 0
                      }
                      className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition ${
                        approvalAction === null &&
                        selectableRows.length > 0
                          ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                          : 'cursor-not-allowed bg-slate-200 text-slate-400'
                      }`}
                    >
                      {approvalAction === 'all' ? (
                        <>
                          <SafeIcon
                            icon={FiLoader}
                            className="animate-spin"
                          />
                          Approving...
                        </>
                      ) : (
                        <>
                          <SafeIcon icon={FiCheckCircle} />
                          Approve All
                        </>
                      )}
                    </button>
              </>
            )}
          </div>
        </footer>
      </motion.section>
    </div>
  );
}